import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  ButtonLabel,
  Buttons,
  ButtonsSeparator,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Input from '../../components/Input/Input';
import Modal from '../../components/Modal/Modal';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import TreeView from '../../components/TreeView/TreeView';
import { BuildRequestBodyParams } from '../../components/TreeView/TreeView.types';
import useTreeViewRowStates from '../../components/TreeView/useTreeViewRowStates';
import { useGetRecipeBases } from '../../services/base/base.service';
import { useGetClasses } from '../../services/class/class.service';
import { useGetRecipeProducts } from '../../services/product/product.service';
import { useFmsProdsReport, useGetRecipe } from '../../services/recipe/recipe.service';
import { useGetRecipeSizes } from '../../services/size/size.service';
import { useSpecsInRecipe } from '../../services/spec/spec.service';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { StyledButtons, StyledMessage } from './Recipes.styles';
import { CLASS_STATUS } from '../../constants/ApiEnums';
import { CSVLink } from 'react-csv';
import rem from '../../utils/rem/rem';

export const Recipes: FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { data: classesData, error: classesError } = useGetClasses(Number(CLASS_STATUS.ACTIVE));
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [specNo, setSpecNo] = useState('');
  const [specNoError, setSpecNoError] = useState('');
  const [fetchRecipeEnabled, setFetchRecipeEnabled] = useState(false);
  const { data: recipeData, error, remove } = useGetRecipe({recipeId: +specNo}, fetchRecipeEnabled);
  const { data: fmProdsData } = useFmsProdsReport(true);

  const headers = [
    { label: 'FProds', key: 'fmsProdNumber' }
  ];

  // Safeguard against null/undefined data
  const csvData = fmProdsData?.data?.map((fmsProdNumber) => ({
    fmsProdNumber: fmsProdNumber?.toString() || '',
  })) || [];

  const closeModal = () => {
    setFetchRecipeEnabled(false);
    setSpecNo('');
    setSpecNoError('');
    setModalOpen(false);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const openModal = () => {
    handleModalOpen();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') navigateToRecipe();
  };
  
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (specNoError) setSpecNoError('');
    setSpecNo(e.target.value);
  };

  const navigateToRecipe = () => {
    if (specNo.trim() === '') return;
    setFetchRecipeEnabled(true);
  };

  useEffect(() => {
    if (recipeData?.data && specNo !== '' && fetchRecipeEnabled) {
      navigate(`/recipes/${specNo}`);
    }
    return () => {
      if (typeof remove === 'function') {
        try {
          remove();
        } catch (e) {
          console.error('Error in cleanup function:', e);
        }
      }
    };
  }, [recipeData, specNo, fetchRecipeEnabled, navigate, remove]);

  useEffect(() => {
    if (error) {
      setSpecNoError('Recipe not found');
      setFetchRecipeEnabled(false);
      setSpecNo('');
      if (typeof remove === 'function') {
        try {
          remove();
        } catch (e) {
          console.error('Error in error cleanup:', e);
        }
      }
    }
  }, [error, remove]);

  useEffect(() => {
    if (classesError) {
      try {
        showToast({
          ...genericErrorShowToastOptions,
          label: 'Error fetching classes.',
          id: 'classFetchError',
          toastType: ToastTypes.Error,
        });
      } catch (e) {
        console.error('Error showing toast:', e);
      }
    }
  }, [classesError]);

  const dataStructure = [
    {
      fetchChildren: useGetRecipeBases,
      idKey: 'classNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row?.description || '',
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.row?.classNo || '' };
      },
    },
    {
      fetchChildren: useGetRecipeSizes,
      idKey: 'baseNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row?.description || '',
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.parentId || '', baseNo: params.row.baseNo };
      },
    },
    {
      fetchChildren: useGetRecipeProducts,
      idKey: 'sizeNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row?.description || '',
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo:  params.parentRows?.[0].id|| '',
          baseNo: params.parentId || '',
          sizeNo: params.row?.sizeNo || '',
        };
      },
    },
    {
      fetchChildren: useSpecsInRecipe,
      idKey: 'productNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row?.description || '',
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0]?.id || '',
          baseNo: params.parentRows?.[1]?.id || '',
          sizeNo: params.parentId || '',
          productNo: params.row?.productNo || '',
        };
      },
    },
    {
      to: (params: BuildRequestBodyParams) => `/recipes/${params.row?.specNo || ''}`,
      idKey: 'specNo',
      buildLabel: (params: BuildRequestBodyParams) => 
        `${params.row?.specNo || ''} - ${params.row?.description || ''}`,
    },
  ];

  const { expandedRows, handleRowClick, reset } = useTreeViewRowStates(dataStructure, 'selectRecipes');

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Recipes' />
      <Page>
        <TableWrapper>
          {classesData?.data && classesData.data.length > 0 ? (
            <TreeView
              rows={classesData.data}
              dataStructure={dataStructure}
              expandedRows={expandedRows}
              onRowClick={handleRowClick}
            />
          ) : (
            <div>No class data available</div>
          )}
        </TableWrapper>
        <Buttons>
          <ButtonLabel>Recipes Actions</ButtonLabel>
          <Button label='Open' testId='openRecipeActionButton' variant={Variant.Primary} onClick={openModal} />
          <Button label='Copy' testId='copyRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/copy`)} />
          <Button label='Compare' testId='compareRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/compare`)} />
          <Button label='Delete' testId='deleteRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/deleterecipes`)} />
          <ButtonsSeparator />
          <ButtonLabel>Raw Product Bulk Actions</ButtonLabel>
          <Button label='Add' testId='addRawProdRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/addrawproducts`)} />
          <Button label='Change' testId='changeRawProdRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/changerawproducts`)} />
          <Button label='Remove' testId='removeRawProdRecipeActionButton' variant={Variant.Secondary} onClick={() => navigate(`/recipes/removerawproducts`)}/>
          <ButtonsSeparator />
          <ButtonLabel>View</ButtonLabel>
          <Button label='Reset' testId='resetViewRecipeActionButton' variant={Variant.Secondary} onClick={reset} />
          <ButtonsSeparator />
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`FProds_${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block', height: rem(40) }}
          >
            <Button variant={Variant.Secondary} label='FProd Report' style={{width: '100%'}}>
            </Button>
          </CSVLink>
        </Buttons>
      </Page>
      <Modal id='openRecipeBySpecNoModal' open={modalOpen} onClose={closeModal} title='Open Recipe'>
        <div>
          <StyledMessage>Enter the spec number you want to open.</StyledMessage>
          <Input
            label='Recipe Spec Number'
            onChange={handleInputChange}
            value={specNo}
            id='openRecipeBySpecNo'
            errorText={specNoError}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          />
          <StyledButtons>
            <Button testId='cancelRecipeModalButton' variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
            <Button testId='openRecipeModalButton' label='Open' onClick={navigateToRecipe} disabled={specNo === ''} />
          </StyledButtons>
        </div>
      </Modal>
    </>
  );
};

export default Recipes;
